import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import apiClient from '../config/config';
import '../assets/css/psearch.css';
import Nav from './Nav';
import SEO from './SEO';

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState({ remaining: 5, total: 5, purchased_credits: 0 });
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('pro');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const pricingTiers = [
    // {
    //   id: 'trial',
    //   name: 'Trial',
    //   description: 'Try it out with a single virtual try-on',
    //   credits: 1,
    //   price: 1.00,
    //   isMonthly: false,
    //   features: ['1 virtual try-on', 'Save to your Fashion Board']
    // },
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for individuals exploring Kashmiri fashion and casual users',
      credits: 10,
      price: 4.99,
      isMonthly: false,
      features: ['10 additional virtual try-ons', 'Save unlimited looks to your Fashion Board']
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Ideal for regular shoppers',
      credits: 50,
      price: 14.99,
      isMonthly: false,
      popular: true,
      features: ['Everything in Starter', '50 additional virtual try-ons', 'Save unlimited looks to your Fashion Board']
    },
    {
      id: 'proplus',
      name: 'Pro+',
      description: 'Tailored for power users, influencers, fashion first people.',
      credits: 999,
      price: 9.99,
      isMonthly: true,
      features: ['All Pro features', 'Unlimited virtual try-ons', 'Save unlimited looks to your Fashion Board']
    }
  ];

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Fetch usage data
        try {
          const res = await apiClient.get('/tryon/check-usage/', {
            params: { user_id: session.user.id }
          });
          setUsageData({ 
            remaining: res.data.remaining, 
            total: res.data.total_free || 5,
            purchased_credits: res.data.purchased_credits || 0
          });
        } catch (err) {
          console.error('Error fetching usage:', err);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    };

    getSession();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleUpgrade = async () => {
    if (!user?.id) {
      alert('Please sign in to upgrade.');
      return;
    }

    const tier = pricingTiers.find(t => t.id === selectedTier);
    if (!tier) return;

    setIsProcessingPayment(true);
    try {
      const baseUrl = window.location.origin;
      const response = await apiClient.post('/stripe/create-checkout/', {
        user_id: user.id,
        user_email: user.email,
        success_url: `${baseUrl}/payment-success`,
        cancel_url: `${baseUrl}/payment-cancelled`,
        tier_id: tier.id,
        credits: tier.credits,
        price: Math.round(tier.price * 100),
        is_subscription: tier.isMonthly
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      alert('Failed to start payment process. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const [activeTab, setActiveTab] = useState('overview');

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="profile-spinner" />
        <style>{`
          .profile-loading { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #faf9f7; }
          .profile-spinner { width: 32px; height: 32px; border: 2px solid #eee; border-top-color: #1a1a1a; border-radius: 50%; animation: spin 0.8s linear infinite; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <SEO title="Your Profile" path="/profile" noindex={true} />
      <style>{`
        .profile-page {
          min-height: 100vh;
          background: #fff;
          font-family: 'Tan Aegean', serif;
        }
        /* Ensure Nav uses consistent font across all pages */
        .profile-page .yb-nav {
          font-family: 'Montserrat', sans-serif !important;
        }
        .profile-page .yb-nav-link {
          font-family: 'Montserrat', sans-serif !important;
        }
        .profile-shell {
          padding: 48px 0 56px;
        }
        .profile-container {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 40px;
        }
        @media (max-width: 768px) {
          .profile-shell {
            padding: 24px 20px 40px;
          }
          .profile-container {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .profile-sidebar { position: relative !important; top: 0 !important; }
        }
        .profile-sidebar {
          background: #fff;
          border-radius: 20px;
          padding: 36px 28px;
          height: fit-content;
          position: sticky;
          top: 90px;
          // border: 1px solid #eee;
          border-right: 2px solid #eee;
          border-radius: 0px;
        }
        .profile-avatar {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          object-fit: cover;
          margin: 0 auto 20px;
          display: block;
          border: 3px solid #f9f7f5;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }
        .profile-name {
          font-size: 22px;
          font-weight: 600;
          color: #000;
          text-align: center;
          margin: 0 0 6px 0;
        }
        .profile-email {
          font-size: 12px;
          color: #888;
          text-align: center;
          margin: 0 0 8px 0;
          word-break: break-all;
        }
        .profile-tier {
          display: block;
          text-align: center;
          margin: 0 auto 28px;
          padding: 6px 16px;
          background: #f9f7f5;
          border-radius: 20px;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #666;
          font-weight: 600;
          width: fit-content;
        }
        .profile-nav {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .profile-nav-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
        }
        .profile-nav-item:hover { background: #f9f7f5; color: #000; }
        .profile-nav-item.active { 
          background: #000;
          color: #fff;
        }
        .profile-nav-item svg { width: 18px; height: 18px; opacity: 0.8; }
        .profile-nav-item.active svg { opacity: 1; }
        .profile-signout {
          margin-top: 28px;
          padding-top: 28px;
          border-top: 1px solid #eee;
        }
        .profile-signout-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 14px;
          color: #999;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          background: none;
          width: 100%;
        }
        .profile-signout-btn:hover { background: #fef5f5; color: #c00; }
        .profile-main {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        .profile-card {
          background: #fff;
          border-radius: 20px;
          padding: 32px;
          // border: 1px solid #eee;
        }
        .profile-card-title {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #999;
          margin: 0 0 24px 0;
          font-weight: 600;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        @media (max-width: 600px) {
          .stats-grid { grid-template-columns: 1fr; }
        }
        .stat-item {
          text-align: center;
          padding: 28px 20px;
          background: #f9f7f5;
          border-radius: 16px;
          transition: all 0.3s ease;
        }
        .stat-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
        }
        .stat-value {
          font-size: 40px;
          font-weight: 300;
          color: #000;
          line-height: 1;
        }
        .stat-label {
          font-size: 11px;
          color: #888;
          margin-top: 10px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .progress-bar {
          height: 6px;
          background: #eee;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 20px;
        }
        .progress-fill {
          height: 100%;
          background: #000;
          border-radius: 3px;
          transition: width 0.5s ease;
        }
        .upgrade-banner {
          background: #000;
          border-radius: 20px;
          padding: 36px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 28px;
          flex-wrap: wrap;
        }
        .upgrade-text {
          position: relative;
          z-index: 1;
        }
        .upgrade-text span {
          display: block;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #999;
          margin-bottom: 10px;
        }
        .upgrade-text h3 {
          font-size: 22px;
          font-weight: 500;
          margin: 0 0 8px 0;
        }
        .upgrade-text p {
          font-size: 14px;
          color: #888;
          margin: 0;
        }
        .upgrade-btn {
          background: #fff;
          color: #000;
          border: none;
          padding: 16px 36px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 1.5px;
        }
        .upgrade-btn:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
        }
        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .settings-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: #f9f7f5;
          border-radius: 16px;
          transition: all 0.3s ease;
        }
        .settings-item:hover { 
          transform: translateX(4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
        }
        .settings-item-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }
        .settings-icon {
          width: 44px;
          height: 44px;
          background: #fff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
        }
        .settings-item-text h4 {
          font-size: 14px;
          font-weight: 500;
          color: #000;
          margin: 0 0 4px 0;
        }
        .settings-item-text p {
          font-size: 12px;
          color: #888;
          margin: 0;
        }
        .settings-action {
          padding: 10px 20px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid #ddd;
          background: #fff;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .settings-action:hover { border-color: #000; color: #000; }
        .settings-action.danger:hover { border-color: #c00; color: #c00; background: #fef5f5; }
        .member-info {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 24px;
          background: #f9f7f5;
          border-radius: 16px;
        }
        .member-badge {
          width: 52px;
          height: 52px;
          background: #000;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 22px;
        }
        .member-details h4 {
          font-size: 15px;
          font-weight: 500;
          color: #000;
          margin: 0 0 4px 0;
        }
        .member-details p {
          font-size: 13px;
          color: #888;
          margin: 0;
        }
      `}</style>

      <div className="container profile-shell">
      <Nav />

      <div className="profile-container">
        {/* Sidebar */}
        <div className="profile-sidebar">
          <img
            src={user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_metadata?.full_name || 'U')}&background=1a1a1a&color=fff&size=80`}
            alt="Profile"
            className="profile-avatar"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-avatar.jpg'; }}
          />
          <h2 className="profile-name">{user?.user_metadata?.full_name || 'Welcome'}</h2>
          <p className="profile-email">{user?.email}</p>
          <span className="profile-tier">Free Member</span>

          <nav className="profile-nav">
            <button className={`profile-nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              Overview
            </button>
            <button className={`profile-nav-item ${activeTab === 'subscription' ? 'active' : ''}`} onClick={() => setActiveTab('subscription')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Subscription
            </button>
            <button className={`profile-nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Settings
            </button>
          </nav>

          <div className="profile-signout">
            <button className="profile-signout-btn" onClick={handleSignOut}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign Out
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="profile-main">
          {activeTab === 'overview' && (
            <>
              {/* Stats Card */}
              <div className="profile-card">
                <h3 className="profile-card-title">Your Usage</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">{usageData.remaining}</div>
                    <div className="stat-label">Try-ons Left</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{usageData.purchased_credits || 0}</div>
                    <div className="stat-label">Purchased Credits</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{usageData.total - usageData.remaining}</div>
                    <div className="stat-label">Used This Period</div>
                  </div>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${(usageData.remaining / usageData.total) * 100}%` }} />
                </div>
              </div>

              {/* Upgrade Banner */}
              <div className="upgrade-banner">
                <div className="upgrade-text">
                  <span>Exclusive</span>
                  <h3>Get More Try-Ons</h3>
                  <p>Unlock unlimited virtual try-ons and save your favorite looks forever.</p>
                </div>
                <button className="upgrade-btn" onClick={() => setShowPricingModal(true)}>
                  View Plans
                </button>
              </div>

              {/* Member Info */}
              <div className="profile-card">
                <h3 className="profile-card-title">Membership</h3>
                <div className="member-info">
                  <div className="member-badge">✦</div>
                  <div className="member-details">
                    <h4>Free Plan</h4>
                    <p>Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'subscription' && (
            <>
              <div className="profile-card">
                <h3 className="profile-card-title">Current Plan</h3>
                <div className="member-info">
                  <div className="member-badge">✦</div>
                  <div className="member-details">
                    <h4>Free Plan</h4>
                    <p>{usageData.remaining} of {usageData.total} free try-ons remaining</p>
                  </div>
                </div>
              </div>

              <div className="upgrade-banner">
                <div className="upgrade-text">
                  <span>Premium</span>
                  <h3>Upgrade Your Experience</h3>
                  <p>Get more try-ons, priority processing, and save unlimited looks.</p>
                </div>
                <button className="upgrade-btn" onClick={() => setShowPricingModal(true)}>
                  View Plans
                </button>
              </div>

              <div className="profile-card">
                <h3 className="profile-card-title">Plan Features</h3>
                <div className="settings-list">
                  {['Unlimited Looks', 'Priority Processing', 'Save Forever', 'Early Access'].map((feature) => (
                    <div key={feature} className="settings-item">
                      <div className="settings-item-left">
                        <div className="settings-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <div className="settings-item-text">
                          <h4>{feature}</h4>
                          <p>Available with Pro plans</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === 'settings' && (
            <>
              <div className="profile-card">
                <h3 className="profile-card-title">Account</h3>
                <div className="settings-list">
                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      </div>
                      <div className="settings-item-text">
                        <h4>Profile Information</h4>
                        <p>{user?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </div>
                      <div className="settings-item-text">
                        <h4>Password & Security</h4>
                        <p>Managed by Google</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-card">
                <h3 className="profile-card-title">Danger Zone</h3>
                <div className="settings-list">
                  <div className="settings-item">
                    <div className="settings-item-left">
                      <div className="settings-icon" style={{ background: '#fff5f5', color: '#e53935' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </div>
                      <div className="settings-item-text">
                        <h4>Delete Account</h4>
                        <p>Permanently remove your account and data</p>
                      </div>
                    </div>
                    <button 
                      className="settings-action danger"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Request Deletion
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="pricing-overlay" onClick={() => setShowPricingModal(false)}>
          <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pricing-header">
              <h1>Unlock More Try-Ons!</h1>
              <p>Choose a plan that works for you</p>
            </div>

            <h2 className="pricing-title">Pricing Plans</h2>

            <div className="pricing-cards">
              {pricingTiers.map((tier) => (
                <div 
                  key={tier.id}
                  className={`pricing-card ${selectedTier === tier.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTier(tier.id)}
                >
                  <div className="card-header">
                    <h3 className="tier-name">{tier.name}</h3>
                    <span className="tier-icon">
                      {tier.id === 'starter' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      )}
                      {tier.id === 'pro' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      )}
                      {tier.id === 'proplus' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-1h14v1z"/>
                        </svg>
                      )}
                    </span>
                  </div>
                  <p className="tier-description">{tier.description}</p>
                  <ul className="tier-features">
                    {tier.features.map((feature, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="tier-price">
                    <span className="price">$ {tier.price.toFixed(2)}</span>
                    {tier.isMonthly && <span className="price-period">/mo</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-actions">
              <button 
                className="upgrade-now-btn"
                onClick={handleUpgrade}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment ? 'Processing...' : 'Upgrade Now'}
              </button>
              <button className="maybe-later-btn" onClick={() => setShowPricingModal(false)}>
                Maybe Later
              </button>
            </div>

            <p className="secure-note">Secure payment powered by Stripe</p>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div className="modal-content" style={{ background: '#fff', padding: '32px 40px', borderRadius: '25px', textAlign: 'center', maxWidth: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" style={{ marginBottom: '16px' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{ fontSize: '18px', color: '#2d2a26', marginBottom: '8px', fontWeight: '500' }}>Delete Account?</p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>Are you sure you want to request account deletion? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => setShowDeleteModal(false)} 
                style={{ padding: '10px 24px', background: '#f5f5f5', color: '#2d2a26', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  window.location.href = `mailto:uzmahm10@gmail.com?subject=Account Deletion Request&body=Please delete my account.%0A%0AEmail: ${user?.email}%0AUser ID: ${user?.id}%0A%0AI understand this action is permanent and all my data will be removed.`;
                }} 
                style={{ padding: '10px 24px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
